/**
 * @file     global-error.tsx
 * @readme   ../../CLAUDE.md
 * @purpose  Root-level error boundary, renders outside root layout with own html/body
 */
'use client';
import { useEffect } from 'react';
import { isChunkLoadError, recoverFromChunkLoadError } from '@/services/error-reporting/chunkLoadError';
import { isCspEvalBlocked } from '@/services/error-reporting/cspEvalBlocked';
import { isElementFromPointClickError } from '@/services/error-reporting/elementFromPointClick';

/**
 * This boundary replaces the root layout, so neither the theme provider nor the
 * boot CSS is on the page. The boot variables it needs are inlined here (values
 * from createThemeBootStyles in src/core/theme/webThemeState.ts) instead of
 * pulling the token barrel into a bundle every page loads.
 */
const GLOBAL_ERROR_CSS = `
:root{--r-bg:#F8F9FB;--r-text:#1D2733;--r-text2:#3D4A5C;--r-primary:#005886;--r-primary-hover:#004A72;--r-text-on-primary:#FFFFFF;--r-border-strong:#BFC8D4;--r-hero-wash:#F2F9FD;--r-focus:#005886;color-scheme:light}
@media (prefers-color-scheme: dark){:root{--r-bg:#080D12;--r-text:#F2F6FA;--r-text2:#B8C6D5;--r-primary:#4DB2BA;--r-primary-hover:#77C6CD;--r-text-on-primary:#060A0F;--r-border-strong:#55657A;--r-hero-wash:#08191C;--r-focus:#4DB2BA;color-scheme:dark}}
body{margin:0;min-height:100vh;font-family:Inter,system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;display:grid;place-items:center;padding:48px 20px;box-sizing:border-box;text-align:center;color:var(--r-text);background:radial-gradient(ellipse 55% 60% at 50% 0%,var(--r-hero-wash),transparent 70%),var(--r-bg);-webkit-font-smoothing:antialiased}
.ge{max-width:640px;display:flex;flex-direction:column;align-items:center;gap:12px}
.ge h1{margin:0;font-size:clamp(32px,5vw,48px);font-weight:560;letter-spacing:-0.025em;line-height:1.08}
.ge p{margin:0;font-size:17px;line-height:1.5;color:var(--r-text2)}
.ge-actions{display:flex;flex-wrap:wrap;justify-content:center;gap:8px;padding-top:12px}
.ge-btn{display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:0 20px;border-radius:12px;font:inherit;font-size:15px;font-weight:500;text-decoration:none;cursor:pointer;transition:background-color 160ms ease,border-color 160ms ease}
.ge-btn:focus-visible{outline:2px solid var(--r-focus);outline-offset:2px}
.ge-primary{border:0;background:var(--r-primary);color:var(--r-text-on-primary)}
.ge-primary:hover{background:var(--r-primary-hover)}
.ge-secondary{border:1px solid var(--r-border-strong);background:transparent;color:var(--r-text)}
@media (prefers-reduced-motion: reduce){.ge-btn{transition:none}}
`;

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Stale chunk after a new deploy — reload to recover, don't report.
    if (isChunkLoadError(error)) {
      recoverFromChunkLoadError();
      return;
    }
    if (isCspEvalBlocked(error)) return;
    if (isElementFromPointClickError(error)) return;
    try {
      void fetch('/api/error-report', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          message: error.message,
          stack: error.stack,
          digest: error.digest,
          boundary: 'global-error',
          url: typeof window !== 'undefined' ? window.location.href : '',
          route: typeof window !== 'undefined' ? window.location.pathname : '',
          userAgent: typeof window !== 'undefined' ? navigator.userAgent : '',
          timestamp: new Date().toISOString(),
        }),
      }).catch(() => {});
    } catch { /* cannot throw */ }
  }, [error]);

  return (
    <html lang="en">
      <head>
        {/* next/font cannot load in a client boundary, so Inter comes from the same Google Fonts origin the CSP already allows. */}
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap" />
        <style dangerouslySetInnerHTML={{ __html: GLOBAL_ERROR_CSS }} />
      </head>
      <body>
        <main className="ge">
          <h1>Something went wrong</h1>
          <p>This page did not load.</p>
          <div className="ge-actions">
            <button type="button" className="ge-btn ge-primary" onClick={reset}>
              Try again
            </button>
            <a className="ge-btn ge-secondary" href="/">
              Home page
            </a>
          </div>
        </main>
      </body>
    </html>
  );
}
